export interface DetectionResult {
  pattern: string;
  severity: 'high' | 'medium' | 'low';
  match: string;
  description: string;
}

export type InjectionRange = {
  start: number;
  end: number;
  severity: 'high' | 'medium' | 'low';
  match: string;
};

export type DetectionPattern = {
  pattern: RegExp;
  severity: 'high' | 'medium' | 'low';
  description: string;
};

export const DETECTION_PATTERNS: DetectionPattern[] = [
  {
    pattern: /ignore\s+(all\s+)?(previous|prior|above|earlier)?\s*instructions?/i,
    severity: 'high',
    description: 'Attempts to override system instructions',
  },
  {
    pattern: /you\s+are\s+now|pretend\s+(to\s+be|you'?re)|act\s+as\s+if/i,
    severity: 'high',
    description: 'Identity manipulation attempt',
  },
  {
    pattern: /<\/?(system|user|assistant|prompt|input|output|instruction)>/i,
    severity: 'high',
    description: 'Delimiter injection detected',
  },
  {
    pattern: /\[?(system|instruction|admin)\]?:/i,
    severity: 'high',
    description: 'Fake system marker injection',
  },
  {
    pattern: /repeat\s+(everything|all|the|what)\s+(above|before|you\s+said)/i,
    severity: 'medium',
    description: 'System prompt extraction attempt',
  },
  {
    pattern: /what\s+(is|are)\s+your\s+(instructions?|rules?|prompt|guidelines?)/i,
    severity: 'medium',
    description: 'Probing for system prompt',
  },
  {
    pattern: /roleplay|let'?s\s+play|imagine\s+you/i,
    severity: 'medium',
    description: 'Roleplay-based bypass attempt',
  },
  {
    pattern: /DAN|jailbreak|do\s+anything\s+now/i,
    severity: 'high',
    description: 'Known jailbreak pattern',
  },
  {
    pattern: /&#\d+;|%[0-9a-f]{2}|\\u[0-9a-f]{4}/i,
    severity: 'medium',
    description: 'Encoded content detected',
  },
  {
    pattern: /forget\s+(everything|all|your)/i,
    severity: 'high',
    description: 'Memory manipulation attempt',
  },
  {
    pattern: /new\s+(instructions?|rules?|prompt)/i,
    severity: 'medium',
    description: 'Instruction override attempt',
  },
  {
    pattern: /reveal|disclose|show\s+me\s+(your|the)\s+(secret|hidden|system)/i,
    severity: 'medium',
    description: 'Information disclosure attempt',
  },
];

const severityOrder = { high: 0, medium: 1, low: 2 };

export function analyzeForInjection(
  input: string,
  patterns: DetectionPattern[] = DETECTION_PATTERNS
): DetectionResult[] {
  const results: DetectionResult[] = [];

  for (const { pattern, severity, description } of patterns) {
    const match = input.match(pattern);
    if (match) {
      results.push({
        pattern: pattern.source,
        severity,
        match: match[0],
        description,
      });
    }
  }

  results.sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);
  return results;
}

export function getGlobalRegex(pattern: RegExp): RegExp {
  const flags = pattern.flags.includes('g') ? pattern.flags : `${pattern.flags}g`;
  return new RegExp(pattern.source, flags);
}

export function findInjectionRanges(
  input: string,
  patterns: DetectionPattern[] = DETECTION_PATTERNS
): InjectionRange[] {
  const ranges: InjectionRange[] = [];

  for (const { pattern, severity } of patterns) {
    const regex = getGlobalRegex(pattern);
    let match: RegExpExecArray | null;
    while ((match = regex.exec(input)) !== null) {
      if (!match[0]) {
        if (regex.lastIndex === match.index) {
          regex.lastIndex += 1;
        }
        continue;
      }
      ranges.push({
        start: match.index,
        end: match.index + match[0].length,
        severity,
        match: match[0],
      });
      if (regex.lastIndex === match.index) {
        regex.lastIndex += 1;
      }
    }
  }

  if (ranges.length === 0) {
    return [];
  }

  ranges.sort((a, b) => {
    if (a.start !== b.start) return a.start - b.start;
    const lengthDiff = b.end - b.start - (a.end - a.start);
    if (lengthDiff !== 0) return lengthDiff;
    return severityOrder[a.severity] - severityOrder[b.severity];
  });

  const filtered: InjectionRange[] = [];
  let lastEnd = -1;
  for (const range of ranges) {
    if (range.start < lastEnd) continue;
    filtered.push(range);
    lastEnd = range.end;
  }

  return filtered;
}
